/**
 * Shared tiered search engine used by BOTH notes and bookmarks.
 *
 * One query → one ranked, deduped hit list across three tiers:
 *   tier 0 = exact substring
 *   tier 1 = token-prefix (shared leading stem >= MIN_STEM chars)
 *   tier 2 = bounded fuzzy (Levenshtein within maxDist)
 *
 * Each document appears once, at its best (lowest) tier. Within a tier, a match
 * in a higher-precedence field (lower fieldIndex) outranks a lower one. The
 * fuzzy tier ignores any token (query or candidate) shorter than MIN_STEM on
 * BOTH sides, which is the "tighten fuzzy to 3 chars min" fix that stops
 * garbage short-fragment matches (e.g. "apple" matching "tab").
 */

const SNIPPET_RADIUS = 40;

export const MIN_STEM = 3;

/**
 * Builds a context snippet around a match in `text`, returning the windowed
 * string plus the match's offsets *within that snippet* for highlighting.
 */
export function buildSnippet(
  text: string,
  matchIndex: number,
  matchLength: number,
  radius: number = SNIPPET_RADIUS,
): { snippet: string; matchStart: number; matchEnd: number } {
  if (matchIndex < 0) {
    return {
      snippet: text.slice(0, radius * 2),
      matchStart: 0,
      matchEnd: 0,
    };
  }
  const start = Math.max(0, matchIndex - radius);
  const end = Math.min(text.length, matchIndex + matchLength + radius);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';
  const snippet = prefix + text.slice(start, end) + suffix;
  const matchStart = prefix.length + (matchIndex - start);
  const matchEnd = matchStart + matchLength;
  return { snippet, matchStart, matchEnd };
}

/** Compiles a case-insensitive regex; returns null if the pattern is invalid. */
export function compileRegex(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern, 'i');
  } catch {
    return null;
  }
}

/** Simple typo tolerance: are `a` and `b` within edit distance `<= max`? */
export function withinEditDistance(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false;
  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return false; // early exit: no cell can recover
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length] <= max;
}

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

/**
 * True when two tokens share a leading stem of at least MIN_STEM chars — i.e.
 * one starts with the other, or they agree on their first MIN_STEM+ chars.
 * This makes "ref"→"refid" and "reference"→"refid"/"refer" both match.
 */
export function sharesPrefix(a: string, b: string): boolean {
  if (a.startsWith(b) || b.startsWith(a)) {
    return Math.min(a.length, b.length) >= MIN_STEM;
  }
  let i = 0;
  const max = Math.min(a.length, b.length);
  while (i < max && a[i] === b[i]) i++;
  return i >= MIN_STEM;
}

/** One searchable field of a document: a name (for highlight key) + its text. */
export interface SearchField {
  name: string;
  text: string;
}

export interface LayeredHit<T> {
  doc: T;
  /** 0 = exact substring, 1 = token-prefix, 2 = fuzzy. Lower is better. */
  tier: number;
  /** Winning field's name. */
  fieldName: string;
  /** Winning field's full text (for snippet/highlight). */
  fieldText: string;
  /** Precedence index of winning field (0 = highest). */
  fieldIndex: number;
  /** Offset of match within fieldText (>= 0, or -1 if none locatable). */
  matchIndex: number;
  /** Length of the matched span (0 for fuzzy with no exact span). */
  matchLength: number;
}

export interface LayeredSearchParams<T> {
  query: string;
  docs: T[];
  /** Ordered fields per doc; first = highest precedence. */
  fieldsOf: (doc: T) => SearchField[];
  /** Dedupe key; each doc appears once at its best (lowest) tier/field. */
  keyOf: (doc: T) => string;
  /** Override fuzzy max edit distance; default q.length >= 5 ? 2 : 1. */
  maxDistOf?: (q: string) => number;
}

/** Locates the first token-prefix occurrence of `token` within `text`. */
function locatePrefix(text: string, token: string): number {
  const cleaned = token.replace(/[^a-z0-9]/gi, '');
  if (!cleaned) return -1;
  return text.toLowerCase().search(new RegExp(`\\b${cleaned}`, 'i'));
}

/**
 * Tiered, deduped search over `docs`. Returns one hit per surviving doc at its
 * best tier/field, sorted by (tier asc, fieldIndex asc). Callers apply their
 * own final tie-breaks (e.g. localeCompare).
 */
export function layeredSearch<T>(
  params: LayeredSearchParams<T>,
): LayeredHit<T>[] {
  const { query, docs, fieldsOf, keyOf } = params;
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const qTokens = tokenize(q);
  const maxDist = (
    params.maxDistOf ?? ((s: string) => (s.length >= 5 ? 2 : 1))
  )(q);

  const hits = new Map<string, LayeredHit<T>>();

  const consider = (hit: LayeredHit<T>) => {
    const key = keyOf(hit.doc);
    const existing = hits.get(key);
    if (existing) {
      if (existing.tier < hit.tier) return;
      if (existing.tier === hit.tier && existing.fieldIndex <= hit.fieldIndex)
        return;
    }
    hits.set(key, hit);
  };

  for (const doc of docs) {
    const fields = fieldsOf(doc);

    // Tier 0: exact substring in field-precedence order.
    let matched = false;
    for (let fi = 0; fi < fields.length; fi++) {
      const idx = fields[fi].text.toLowerCase().indexOf(q);
      if (idx >= 0) {
        consider({
          doc,
          tier: 0,
          fieldName: fields[fi].name,
          fieldText: fields[fi].text,
          fieldIndex: fi,
          matchIndex: idx,
          matchLength: q.length,
        });
        matched = true;
        break;
      }
    }
    if (matched) continue;

    // Tier 1: token-prefix. Highest-precedence field that contains a word
    // sharing a >= MIN_STEM leading stem with any query token.
    for (let fi = 0; fi < fields.length; fi++) {
      const words = tokenize(fields[fi].text);
      const hitTok = qTokens.find((qt) =>
        words.some((w) => sharesPrefix(qt, w)),
      );
      if (hitTok) {
        const idx = locatePrefix(fields[fi].text, hitTok);
        consider({
          doc,
          tier: 1,
          fieldName: fields[fi].name,
          fieldText: fields[fi].text,
          fieldIndex: fi,
          matchIndex: idx >= 0 ? idx : 0,
          matchLength: hitTok.length,
        });
        matched = true;
        break;
      }
    }
    if (matched) continue;

    // Tier 2: bounded fuzzy. Skip any token shorter than MIN_STEM on BOTH
    // sides (query token AND candidate word) before considering edit distance.
    for (let fi = 0; fi < fields.length; fi++) {
      const words = tokenize(fields[fi].text);
      const fuzzy = qTokens.some(
        (qt) =>
          qt.length >= MIN_STEM &&
          words.some(
            (w) => w.length >= MIN_STEM && withinEditDistance(qt, w, maxDist),
          ),
      );
      if (fuzzy) {
        consider({
          doc,
          tier: 2,
          fieldName: fields[fi].name,
          fieldText: fields[fi].text,
          fieldIndex: fi,
          matchIndex: -1,
          matchLength: 0,
        });
        break;
      }
    }
  }

  return Array.from(hits.values()).sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    return a.fieldIndex - b.fieldIndex;
  });
}
